import { round } from 'lodash'
import { t } from 'i18next'
import {
  AnyEnergySystemParametersInput,
  CreateEnergySystemInput,
  EnergySystemFragment,
  EnergySystemParametersFragment,
  EnergySystemType,
  UpdateEnergySystemInput,
  VentilationDeviceType,
} from '../../../generated/graphql'
import { formatPercent, formatPower } from '../../../util/formatters'
import {
  parseDecimal,
  parseKwToW,
  parsePercentToDecimal,
} from '../../../util/parsers'

export type DeviceData = {
  deviceName: string
  effectedArea: string
  airFlow: string
  deviceScheduleId: string | undefined
  heatRecoveryEfficiency: string
  SFP: string
  systemType: VentilationDeviceType
  demandBasedUsageCoefficient: string
  exhaustAirMinimumTemp: string
  coolingCoil: string
  supplyAirTemp: string
  indoorAirTemp: string
  returnAirTemp: string
  chilldrop: string
  chilldropTempLimit: string
  exhaustAirHeatPump: string
}

type FormValuesParameters = {
  domesticHotWater?: {
    consumptionDensity: string
    distributionEfficiency: string
    consumptionFactor: string
    specificLength: string
    specificHeatLoss: string
    heatersAmount: string
    heaterPower: string
    wasteWaterHeatContentRatio: string
  }
  ventilation?: {
    deviceData?: DeviceData[]
    heatingDistributionEfficiency: string
    heatPumpExhaustAirMinimumTemp: string
  }
  cooling?: {
    systemCoolingPower: string
    scStartTemp: string
    scIncreaseSlope: string
    scIncreaseExtraCoef: string
    scExtraCoefStartTemp: string
    scDistributionEfficiency: string
    scSpacesPortion: string
    acDemandDisplacementConstant: string
    acCorrectionCoef: string
    acIncreaseExtraCoef: string
    acExtraCoefStartTemp: string
    acDistributionEfficiency: string
  }
  heatLoad?: {
    occupantHeatLoad: string
    occupantHeatLoadUtilFactor: string
    occupantScheduleId: string | undefined
    applianceHeatLoad: string
    applianceHeatLoadUtilFactor: string
    applianceScheduleId: string | undefined
    lightingHeatLoad: string
    lightingHeatLoadUtilFactor: string
    lightingScheduleId: string | undefined
    heatLoadUtilStartTemp: string
    affectingSpaceHeatingStartTemp: string
  }
  electConsumption?: {
    electricityBaseLoad: string
    occupantLightingElShare: string
    occupantApplianceElShare: string
    occupantVentilationElShare: string
    occupantBaseLoadElShare: string
  }
  spaceHeating?: {
    exteriorWallArea: string
    roofArea: string
    baseFloorArea: string
    windowsArea: string
    doorsArea: string
    exteriorWallUvalue: string
    roofUvalue: string
    baseFloorUvalue: string
    windowsUvalue: string
    doorsUvalue: string
    heatBridgeRatio: string
    q50: string
    airLeakageXfactor: string
    heatSupplyEfficiency: string
    basEfficiency: string
    affectingHeatLossStartTemp: string
    dynamicOutTempWeightFactor: string
    heatLossesCorrectionFactor: string
    heatLossesCorrectionFactorStartTemp: string
    supplyAirHeatingStartTemp: string
    supplyAirFullHeatingStartTemp: string
  }
}

export type FormValues = {
  name: string
  type: EnergySystemType
  scheduleId: string | undefined
  parameters: FormValuesParameters
  modelArea: number
}

const convertFormParametersToInputParameters = (
  parameters: FormValuesParameters,
): AnyEnergySystemParametersInput => ({
  domesticHotWater: parameters.domesticHotWater
    ? {
        consumptionDensity: parseKwToW(
          parameters.domesticHotWater.consumptionDensity,
        ),
        distributionEfficiency: parseDecimal(
          parameters.domesticHotWater.distributionEfficiency,
        ),
        consumptionFactor: parseDecimal(
          parameters.domesticHotWater.consumptionFactor,
        ),
        circulationSpecificLength: parseDecimal(
          parameters.domesticHotWater.specificLength,
        ),
        circulationSpecificHeatLoss: parseDecimal(
          parameters.domesticHotWater.specificHeatLoss,
        ),
        circulationHeatersAmount: Number(
          parameters.domesticHotWater.heatersAmount,
        ),
        circulationHeatersPower: parseDecimal(
          parameters.domesticHotWater.heaterPower,
        ),
        wasteWaterHeatContentRatio: parseDecimal(
          parameters.domesticHotWater.wasteWaterHeatContentRatio,
        ),
      }
    : undefined,
  ventilation: parameters.ventilation
    ? {
        deviceData: parameters.ventilation.deviceData
          ? parameters.ventilation.deviceData.map((d) => ({
              deviceName: d.deviceName,
              effectedArea: d.effectedArea,
              airFlow: parseDecimal(d.airFlow),
              deviceScheduleId: d.deviceScheduleId,
              heatRecoveryEfficiency: parseDecimal(d.heatRecoveryEfficiency),
              SFP: parseDecimal(d.SFP),
              systemType: d.systemType ?? undefined,
              demandBasedUsageCoefficient: parseDecimal(
                d.demandBasedUsageCoefficient,
              ),
              exhaustAirMinimumTemp: parseDecimal(d.exhaustAirMinimumTemp),
              coolingCoil: parseInt(d.coolingCoil, 2),
              supplyAirTemp: parseDecimal(d.supplyAirTemp),
              indoorAirTemp: parseDecimal(d.indoorAirTemp),
              returnAirTemp: parseDecimal(d.returnAirTemp),
              chilldrop: parseDecimal(d.chilldrop),
              chilldropTempLimit: parseDecimal(d.chilldropTempLimit),
              exhaustAirHeatPump: parseInt(d.exhaustAirHeatPump, 2),
            }))
          : [],
        heatingDistributionEfficiency: parseDecimal(
          parameters.ventilation.heatingDistributionEfficiency,
        ),
        heatPumpExhaustAirMinimumTemp: parseDecimal(
          parameters.ventilation.heatPumpExhaustAirMinimumTemp,
        ),
      }
    : undefined,
  cooling: parameters.cooling
    ? {
        systemCoolingPower: parseKwToW(parameters.cooling.systemCoolingPower),
        scStartTemp: parseDecimal(parameters.cooling.scStartTemp),
        scIncreaseSlope: parseDecimal(parameters.cooling.scIncreaseSlope),
        scIncreaseExtraCoef: parseDecimal(
          parameters.cooling.scIncreaseExtraCoef,
        ),
        scExtraCoefStartTemp: parseDecimal(
          parameters.cooling.scExtraCoefStartTemp,
        ),
        scDistributionEfficiency: parseDecimal(
          parameters.cooling.scDistributionEfficiency,
        ),
        scSpacesPortion: parseDecimal(parameters.cooling.scSpacesPortion),
        acDemandDisplacementConstant: parseDecimal(
          parameters.cooling.acDemandDisplacementConstant,
        ),
        acCorrectionCoef: parseDecimal(parameters.cooling.acCorrectionCoef),
        acIncreaseExtraCoef: parseDecimal(
          parameters.cooling.acIncreaseExtraCoef,
        ),
        acExtraCoefStartTemp: parseDecimal(
          parameters.cooling.acExtraCoefStartTemp,
        ),
        acDistributionEfficiency: parseDecimal(
          parameters.cooling.acDistributionEfficiency,
        ),
      }
    : undefined,
  heatLoad: parameters.heatLoad
    ? {
        occupantHeatLoad: parseDecimal(parameters.heatLoad.occupantHeatLoad),
        occupantHeatLoadUtilFactor: parseDecimal(
          parameters.heatLoad.occupantHeatLoadUtilFactor,
        ),
        occupantScheduleId: parameters.heatLoad.occupantScheduleId ?? '',
        applianceHeatLoad: parseDecimal(parameters.heatLoad.applianceHeatLoad),
        applianceHeatLoadUtilFactor: parseDecimal(
          parameters.heatLoad.applianceHeatLoadUtilFactor,
        ),
        applianceScheduleId: parameters.heatLoad.applianceScheduleId ?? '',
        lightingHeatLoad: parseDecimal(parameters.heatLoad.lightingHeatLoad),
        lightingHeatLoadUtilFactor: parseDecimal(
          parameters.heatLoad.lightingHeatLoadUtilFactor,
        ),
        lightingScheduleId: parameters.heatLoad.lightingScheduleId ?? '',
        heatLoadUtilStartTemp: parseDecimal(
          parameters.heatLoad.heatLoadUtilStartTemp,
        ),
        affectingSpaceHeatingStartTemp: parseDecimal(
          parameters.heatLoad.affectingSpaceHeatingStartTemp,
        ),
      }
    : undefined,
  electricityConsumption: parameters.electConsumption
    ? {
        electricityBaseLoad: parseKwToW(
          parameters.electConsumption.electricityBaseLoad,
        ),
        occupantLightingElShare: parsePercentToDecimal(
          parameters.electConsumption.occupantLightingElShare,
        ),
        occupantApplianceElShare: parsePercentToDecimal(
          parameters.electConsumption.occupantApplianceElShare,
        ),
        occupantVentilationElShare: parsePercentToDecimal(
          parameters.electConsumption.occupantVentilationElShare,
        ),
        occupantBaseLoadElShare: parsePercentToDecimal(
          parameters.electConsumption.occupantBaseLoadElShare,
        ),
      }
    : undefined,
  spaceHeating: parameters.spaceHeating
    ? {
        exteriorWallArea: parseDecimal(
          parameters.spaceHeating.exteriorWallArea,
        ),
        roofArea: parseDecimal(parameters.spaceHeating.roofArea),
        baseFloorArea: parseDecimal(parameters.spaceHeating.baseFloorArea),
        windowsArea: parseDecimal(parameters.spaceHeating.windowsArea),
        doorsArea: parseDecimal(parameters.spaceHeating.doorsArea),
        exteriorWallUvalue: parseDecimal(
          parameters.spaceHeating.exteriorWallUvalue,
        ),
        roofUvalue: parseDecimal(parameters.spaceHeating.roofUvalue),
        baseFloorUvalue: parseDecimal(parameters.spaceHeating.baseFloorUvalue),
        windowsUvalue: parseDecimal(parameters.spaceHeating.windowsUvalue),
        doorsUvalue: parseDecimal(parameters.spaceHeating.doorsUvalue),
        heatBridgeRatio: parsePercentToDecimal(
          parameters.spaceHeating.heatBridgeRatio,
        ),
        q50: parseDecimal(parameters.spaceHeating.q50),
        airLeakageXfactor: parseDecimal(
          parameters.spaceHeating.airLeakageXfactor,
        ),
        heatSupplyEfficiency: parseDecimal(
          parameters.spaceHeating.heatSupplyEfficiency,
        ),
        basEfficiency: parseDecimal(parameters.spaceHeating.basEfficiency),
        affectingHeatLossStartTemp: parseDecimal(
          parameters.spaceHeating.affectingHeatLossStartTemp,
        ),
        dynamicOutTempWeightFactor: parsePercentToDecimal(
          parameters.spaceHeating.dynamicOutTempWeightFactor,
        ),
        heatLossesCorrectionFactor: parsePercentToDecimal(
          parameters.spaceHeating.heatLossesCorrectionFactor,
        ),
        heatLossesCorrectionFactorStartTemp: parseDecimal(
          parameters.spaceHeating.heatLossesCorrectionFactorStartTemp,
        ),
        supplyAirHeatingStartTemp: parseDecimal(
          parameters.spaceHeating.supplyAirHeatingStartTemp,
        ),
        supplyAirFullHeatingStartTemp: parseDecimal(
          parameters.spaceHeating.supplyAirFullHeatingStartTemp,
        ),
      }
    : undefined,
})

export const mapFormValuesToInput = ({
  name,
  type,
  scheduleId,
  parameters,
}: FormValues): CreateEnergySystemInput | UpdateEnergySystemInput => ({
  name,
  type,
  scheduleId,
  parameters: convertFormParametersToInputParameters(parameters),
})

export const convertInputParametersToFormParameters = (
  parameters: EnergySystemParametersFragment,
): FormValuesParameters => ({
  domesticHotWater:
    parameters.__typename === 'DomesticHotWaterParameters'
      ? {
          consumptionDensity: formatPower(parameters.consumptionDensity, {
            omitUnit: true,
          }),
          distributionEfficiency: String(parameters.distributionEfficiency),
          consumptionFactor: String(parameters.consumptionFactor),
          specificLength: String(parameters.circulationSpecificLength),
          specificHeatLoss: String(parameters.circulationSpecificHeatLoss),
          heatersAmount: String(parameters.circulationHeatersAmount),
          heaterPower: String(parameters.circulationHeatersPower),
          wasteWaterHeatContentRatio: String(
            parameters.wasteWaterHeatContentRatio,
          ),
        }
      : undefined,
  ventilation:
    parameters.__typename === 'VentilationParameters'
      ? {
          deviceData: parameters.deviceData.map((d) => ({
            deviceName: d.deviceName,
            effectedArea: d.effectedArea,
            airFlow: String(d.airFlow),
            deviceScheduleId: d.deviceScheduleId,
            heatRecoveryEfficiency: String(d.heatRecoveryEfficiency),
            SFP: String(d.SFP),
            systemType: d.systemType ?? undefined,
            demandBasedUsageCoefficient: String(d.demandBasedUsageCoefficient),
            exhaustAirMinimumTemp: String(d.exhaustAirMinimumTemp),
            coolingCoil: String(d.coolingCoil),
            supplyAirTemp: String(d.supplyAirTemp),
            indoorAirTemp: String(d.indoorAirTemp),
            returnAirTemp: String(d.returnAirTemp),
            chilldrop: String(d.chilldrop),
            chilldropTempLimit: String(d.chilldropTempLimit),
            exhaustAirHeatPump: String(d.exhaustAirHeatPump),
          })),
          heatingDistributionEfficiency: String(
            parameters.heatingDistributionEfficiency,
          ),
          heatPumpExhaustAirMinimumTemp: String(
            parameters.heatPumpExhaustAirMinimumTemp,
          ),
        }
      : undefined,
  cooling:
    parameters.__typename === 'CoolingParameters'
      ? {
          systemCoolingPower: formatPower(parameters.systemCoolingPower, {
            omitUnit: true,
          }),
          scStartTemp: String(parameters.scStartTemp),
          scIncreaseSlope: String(parameters.scIncreaseSlope),
          scIncreaseExtraCoef: String(parameters.scIncreaseExtraCoef),
          scExtraCoefStartTemp: String(parameters.scExtraCoefStartTemp),
          scDistributionEfficiency: String(parameters.scDistributionEfficiency),
          scSpacesPortion: String(parameters.scSpacesPortion),
          acDemandDisplacementConstant: String(
            parameters.acDemandDisplacementConstant,
          ),
          acCorrectionCoef: String(parameters.acCorrectionCoef),
          acIncreaseExtraCoef: String(parameters.acIncreaseExtraCoef),
          acExtraCoefStartTemp: String(parameters.acExtraCoefStartTemp),
          acDistributionEfficiency: String(parameters.acDistributionEfficiency),
        }
      : undefined,
  heatLoad:
    parameters.__typename === 'HeatLoadParameters'
      ? {
          occupantHeatLoad: String(parameters.occupantHeatLoad),
          occupantHeatLoadUtilFactor: String(
            parameters.occupantHeatLoadUtilFactor,
          ),
          occupantScheduleId: parameters.occupantScheduleId ?? undefined,
          applianceHeatLoad: String(parameters.applianceHeatLoad),
          applianceHeatLoadUtilFactor: String(
            parameters.applianceHeatLoadUtilFactor,
          ),
          applianceScheduleId: parameters.applianceScheduleId ?? undefined,
          lightingHeatLoad: String(parameters.lightingHeatLoad),
          lightingHeatLoadUtilFactor: String(
            parameters.lightingHeatLoadUtilFactor,
          ),
          lightingScheduleId: parameters.lightingScheduleId ?? undefined,
          heatLoadUtilStartTemp: String(parameters.heatLoadUtilStartTemp),
          affectingSpaceHeatingStartTemp: String(
            parameters.affectingSpaceHeatingStartTemp,
          ),
        }
      : undefined,
  electConsumption:
    parameters.__typename === 'ElectricityConsumptionParameters'
      ? {
          electricityBaseLoad: formatPower(parameters.electricityBaseLoad, {
            omitUnit: true,
          }),
          occupantLightingElShare: formatPercent(
            parameters.occupantLightingElShare,
            { omitUnit: true },
          ),
          occupantApplianceElShare: formatPercent(
            parameters.occupantApplianceElShare,
            { omitUnit: true },
          ),
          occupantVentilationElShare: formatPercent(
            parameters.occupantVentilationElShare,
            { omitUnit: true },
          ),
          occupantBaseLoadElShare: formatPercent(
            parameters.occupantBaseLoadElShare,
            { omitUnit: true },
          ),
        }
      : undefined,
  spaceHeating:
    parameters.__typename === 'SpaceHeatingParameters'
      ? {
          exteriorWallArea: String(
            round(Number(parameters.exteriorWallArea), 1),
          ),
          roofArea: String(round(Number(parameters.roofArea), 1)),
          baseFloorArea: String(round(Number(parameters.baseFloorArea), 1)),
          windowsArea: String(round(Number(parameters.windowsArea), 1)),
          doorsArea: String(round(Number(parameters.doorsArea), 1)),
          exteriorWallUvalue: String(parameters.exteriorWallUvalue),
          roofUvalue: String(parameters.roofUvalue),
          baseFloorUvalue: String(parameters.baseFloorUvalue),
          windowsUvalue: String(parameters.windowsUvalue),
          doorsUvalue: String(parameters.doorsUvalue),
          heatBridgeRatio: formatPercent(parameters.heatBridgeRatio, {
            omitUnit: true,
          }),
          q50: String(parameters.q50),
          airLeakageXfactor: String(parameters.airLeakageXfactor),
          heatSupplyEfficiency: String(parameters.heatSupplyEfficiency),
          basEfficiency: String(parameters.basEfficiency),
          affectingHeatLossStartTemp: String(
            parameters.affectingHeatLossStartTemp,
          ),
          dynamicOutTempWeightFactor: formatPercent(
            parameters.dynamicOutTempWeightFactor,
            { omitUnit: true },
          ),
          heatLossesCorrectionFactor: formatPercent(
            parameters.heatLossesCorrectionFactor,
            { omitUnit: true },
          ),
          heatLossesCorrectionFactorStartTemp: String(
            parameters.heatLossesCorrectionFactorStartTemp,
          ),
          supplyAirHeatingStartTemp: String(
            parameters.supplyAirHeatingStartTemp,
          ),
          supplyAirFullHeatingStartTemp: String(
            parameters.supplyAirFullHeatingStartTemp,
          ),
        }
      : undefined,
})

export const mapInputToFormValues = (
  { name, type, schedule, parameters }: EnergySystemFragment,
  modelArea: number,
): FormValues => ({
  name,
  type,
  scheduleId: schedule?.id ?? undefined,
  parameters: convertInputParametersToFormParameters(parameters),
  modelArea,
})

export const deviceDefaultValues: DeviceData = {
  deviceName: 'TKxx',
  effectedArea: 'palvelualue',
  airFlow: '1,0',
  deviceScheduleId: undefined,
  heatRecoveryEfficiency: '0,75',
  SFP: '1,8',
  systemType: VentilationDeviceType.SupplyAndExhaust,
  demandBasedUsageCoefficient: '1',
  exhaustAirMinimumTemp: '-15',
  coolingCoil: '0',
  supplyAirTemp: '20',
  indoorAirTemp: '21',
  returnAirTemp: '22',
  chilldrop: '0',
  chilldropTempLimit: '-15',
  exhaustAirHeatPump: '0',
}

export const defaultValuesByType = (
  type: EnergySystemType,
): Partial<FormValues> => {
  switch (type) {
    case EnergySystemType.DomesticHotWater:
      return {
        name: `${t('energySystemType.DomesticHotWater')}`,
        type: EnergySystemType.DomesticHotWater,
        scheduleId: undefined,
        parameters: {
          domesticHotWater: {
            consumptionDensity: '0',
            distributionEfficiency: '0',
            consumptionFactor: '1',
            specificLength: '0',
            specificHeatLoss: '0',
            heatersAmount: '0',
            heaterPower: '0',
            wasteWaterHeatContentRatio: '0',
          },
        },
      }
    case EnergySystemType.Ventilation:
      return {
        name: `${t('energySystemType.Ventilation')}`,
        type: EnergySystemType.Ventilation,
        scheduleId: undefined,
        parameters: {
          ventilation: {
            deviceData: [], // [{ ...deviceDefaultValues }],
            heatingDistributionEfficiency: '1',
            heatPumpExhaustAirMinimumTemp: '4',
          },
        },
      }
    case EnergySystemType.Cooling:
      return {
        name: `${t('energySystemType.Cooling')}`,
        type: EnergySystemType.Cooling,
        scheduleId: undefined,
        parameters: {
          cooling: {
            systemCoolingPower: '0',
            scStartTemp: '16,5',
            scIncreaseSlope: '0,8',
            scIncreaseExtraCoef: '0',
            scExtraCoefStartTemp: '25',
            scDistributionEfficiency: '1',
            scSpacesPortion: '0,8',
            acDemandDisplacementConstant: '-2',
            acCorrectionCoef: '1,8',
            acIncreaseExtraCoef: '0',
            acExtraCoefStartTemp: '25',
            acDistributionEfficiency: '1',
          },
        },
      }
    case EnergySystemType.HeatLoad:
      return {
        name: `${t('energySystemType.HeatLoad')}`,
        type: EnergySystemType.HeatLoad,
        scheduleId: undefined,
        parameters: {
          heatLoad: {
            occupantHeatLoad: '0',
            occupantHeatLoadUtilFactor: '0',
            occupantScheduleId: undefined,
            applianceHeatLoad: '0',
            applianceHeatLoadUtilFactor: '0',
            applianceScheduleId: undefined,
            lightingHeatLoad: '0',
            lightingHeatLoadUtilFactor: '0',
            lightingScheduleId: undefined,
            heatLoadUtilStartTemp: '-10',
            affectingSpaceHeatingStartTemp: '5',
          },
        },
      }
    case EnergySystemType.ElectricityConsumption:
      return {
        name: `${t('energySystemType.ElectricityConsumption')}`,
        type: EnergySystemType.ElectricityConsumption,
        scheduleId: undefined,
        parameters: {
          electConsumption: {
            electricityBaseLoad: '0',
            occupantLightingElShare: '50',
            occupantApplianceElShare: '100',
            occupantVentilationElShare: '0',
            occupantBaseLoadElShare: '60',
          },
        },
      }
    case EnergySystemType.SpaceHeating:
      return {
        name: `${t('energySystemType.SpaceHeating')}`,
        type: EnergySystemType.SpaceHeating,
        scheduleId: undefined,
        parameters: {
          spaceHeating: {
            exteriorWallArea: '0',
            roofArea: '0',
            baseFloorArea: '0',
            windowsArea: '0',
            doorsArea: '0',
            exteriorWallUvalue: '0.17',
            roofUvalue: '0.09',
            baseFloorUvalue: '0.16',
            windowsUvalue: '1',
            doorsUvalue: '1',
            heatBridgeRatio: '10',
            q50: '2',
            airLeakageXfactor: '15',
            heatSupplyEfficiency: '0.9',
            basEfficiency: '0.95',
            affectingHeatLossStartTemp: '8',
            dynamicOutTempWeightFactor: '0',
            heatLossesCorrectionFactor: '100',
            heatLossesCorrectionFactorStartTemp: '-12',
            supplyAirHeatingStartTemp: '15',
            supplyAirFullHeatingStartTemp: '11',
          },
        },
      }
    default:
      return {
        name: `${t('energySystemType.DomesticHotWater')}`,
        type: EnergySystemType.DomesticHotWater,
        scheduleId: undefined,
        parameters: {
          domesticHotWater: {
            consumptionDensity: '0',
            distributionEfficiency: '0',
            consumptionFactor: '1',
            specificLength: '0',
            specificHeatLoss: '0',
            heatersAmount: '0',
            heaterPower: '0',
            wasteWaterHeatContentRatio: '0',
          },
        },
      }
  }
}
